import { TenantThemeComponent } from '@xeetr/types/models'
import { Trash2 } from 'lucide-react'
import { useState } from 'react'
import { Controller, useFieldArray, useFormContext } from 'react-hook-form'

import { Button } from '@/components/ui/button'
import { ColorPicker } from '@/components/ui/color-picker'
import { Input } from '@/components/ui/input'

const primaryColors = [
  { name: 'primary-color', label: 'Primärfarbe' },
  { name: 'secondary-color', label: 'Sekundärfarbe' },
  { name: 'background-color', label: 'Hintergrundfarbe' },
]

const textColors = [
  { name: 'primary-font-color', label: 'Primäre Schriftfarbe' },
  { name: 'default-font-color', label: 'Standard Schriftfarbe' },
  { name: 'hover-font-color', label: 'Hover Schriftfarbe' },
]

const interactionColors = [
  { name: 'hover-color', label: 'Hover-Farbe' },
  { name: 'input-bg', label: 'Eingabefeld Hintergrund' },
]

const effectColors = [
  { name: 'lifted-shadow', label: 'Angehobener Schatten' },
  { name: 'lifted-shadow-focus', label: 'Angehobener Schatten Fokus' },
]

const TenantTheme = () => {
  const { control } = useFormContext()
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'tenant.theme',
  })
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({})

  type FieldType = TenantThemeComponent & { id: string }

  const validateColor = (color: string): boolean => {
    const hexColorRegex = /^#[0-9a-fA-F]{3}$|^#[0-9a-fA-F]{6}$/
    return hexColorRegex.test(color)
  }

  const setValidationError = (colorName: string, error: string | null) => {
    setValidationErrors((prev) => {
      const newErrors = { ...prev }
      if (error) {
        newErrors[colorName] = error
      } else {
        delete newErrors[colorName]
      }
      return newErrors
    })
  }

  const addColor = (name: string) => {
    if (!fields.find((item) => (item as FieldType).name === name)) {
      append({ name, color: '#000000' })
    }
  }

  const removeColor = (name: string) => {
    const index = fields.findIndex((item) => (item as FieldType).name === name)
    if (index !== -1) {
      remove(index)
    }
  }

  const renderColorSection = (colors: typeof primaryColors) => (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
      {colors.map((color) => {
        const themeIndex = fields.findIndex((item) => (item as FieldType).name === color.name)
        const themeItem = themeIndex !== -1 ? fields[themeIndex] : null
        return (
          <div key={color.name} className="rounded-lg border p-4">
            <div className="mb-3 flex items-center justify-between">
              <label className="text-sm font-medium text-gray-900">{color.label}</label>
              {themeItem && (
                <Button
                  variant="destructive"
                  onClick={() => removeColor(color.name)}
                  size="icon"
                  className="h-8 w-8 shrink-0"
                >
                  <Trash2 className="h-3 w-3" />
                </Button>
              )}
            </div>
            {themeItem ? (
              <div className="space-y-3">
                <div className="flex items-start gap-2">
                  <Controller
                    name={`tenant.theme.${themeIndex}.color`}
                    control={control}
                    render={({ field }) => (
                      <ColorPicker
                        value={field.value}
                        onChange={(colorValue) => {
                          field.onChange(colorValue)
                          setValidationError(color.name, null)
                        }}
                      />
                    )}
                  />
                  <Controller
                    name={`tenant.theme.${themeIndex}.color`}
                    control={control}
                    render={({ field }) => (
                      <div className="flex-1">
                        {validationErrors[color.name] && (
                          <div className="mb-1 text-xs text-red-500">
                            {validationErrors[color.name]}
                          </div>
                        )}
                        <Input
                          value={field.value}
                          onChange={(e) => {
                            const value = e.target.value.startsWith('#')
                              ? e.target.value
                              : '#' + e.target.value
                            field.onChange(value)
                            if (validateColor(value)) {
                              setValidationError(color.name, null)
                            } else {
                              setValidationError(
                                color.name,
                                'Bitte geben Sie eine gültige Hex-Farbe ein (z.B. #ffffff).'
                              )
                            }
                          }}
                          className={`${validationErrors[color.name] ? 'border-red-500' : ''}`}
                        />
                      </div>
                    )}
                  />
                </div>
                <Controller
                  name={`tenant.theme.${themeIndex}.ligthen`}
                  control={control}
                  render={({ field }) => (
                    <Input
                      type="number"
                      value={field.value?.toString() || ''}
                      onChange={(e) => field.onChange(parseInt(e.target.value) || 0)}
                      placeholder="Aufhellen (%)"
                      className="w-full"
                    />
                  )}
                />
              </div>
            ) : (
              <Button onClick={() => addColor(color.name)} size="sm" className="w-full">
                Hinzufügen
              </Button>
            )}
          </div>
        )
      })}
    </div>
  )

  return (
    <div className="space-y-10">
      {/* Primary Colors */}
      <section>
        <h2 className="mb-1 text-lg font-semibold text-gray-900">Hauptfarben</h2>
        <p className="mb-6 text-sm text-gray-600">
          Definieren Sie die grundlegenden Farben Ihrer Marke
        </p>
        {renderColorSection(primaryColors)}
      </section>

      {/* Text Colors */}
      <section>
        <h2 className="mb-1 text-lg font-semibold text-gray-900">Schriftfarben</h2>
        <p className="mb-6 text-sm text-gray-600">
          Konfigurieren Sie die Textfarben für eine optimale Lesbarkeit
        </p>
        {renderColorSection(textColors)}
      </section>

      {/* Interaction Colors */}
      <section>
        <h2 className="mb-1 text-lg font-semibold text-gray-900">Interaktionsfarben</h2>
        <p className="mb-6 text-sm text-gray-600">
          Farben für Hover-Effekte und Eingabefelder
        </p>
        {renderColorSection(interactionColors)}
      </section>

      {/* Effect Colors */}
      <section>
        <h2 className="mb-1 text-lg font-semibold text-gray-900">Effekte</h2>
        <p className="mb-6 text-sm text-gray-600">
          Schatten und visuelle Effekte für Tiefe und Fokus
        </p>
        {renderColorSection(effectColors)}
      </section>
    </div>
  )
}

export default TenantTheme
